import {spotheroPost} from 'api';
import {type ApiResponse} from 'api/api.types';
import {type UpcomingEvent} from 'api/events/events.types';
import {type Prettify} from 'types/helpers.types';
import {parsePaginationMeta} from 'utils/api';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

import {
    type BatchCompPass,
    type CompPassRecipient,
    type CreateCompPassResponse,
    type RawCreateCompPassResponse,
} from 'api/comp-passes/comp-pass.types';

interface CreateCompPassRequestParams {
    events: UpcomingEvent[];
    recipients: CompPassRecipient[];
}

/**
 * Formats the request data for creating comp passes.
 */
const formatCreateCompPassRequest = ({
    events,
    recipients,
}: CreateCompPassRequestParams) => {
    const formattedEvents = events.map((event) => event.eventId);
    const formattedRecipients = recipients.map((recipient) => {
        return {
            email: recipient.email,
            ccEmails: recipient.ccEmail ? [recipient.ccEmail] : [],
            phoneNumber: recipient.phoneNumber ? recipient.phoneNumber : null,
            licensePlate: recipient.licensePlate
                ? recipient.licensePlate
                : null,
            quantity: recipient.quantity.toString(),
        };
    });
    const formattedData = convertKeysToSnakeCase({
        eventIds: formattedEvents,
        recipients: formattedRecipients,
    });

    return formattedData;
};

/**
 * Formats the response from creating comp passes.
 */
const formatCreateCompPassesResponse = (
    response: ApiResponse<RawCreateCompPassResponse[]>
) => {
    const formattedData = camelizeKeys<
        RawCreateCompPassResponse[],
        BatchCompPass[]
    >(response.data.data.results || []);
    const parsedMeta = parsePaginationMeta(response.data.meta);

    const formattedResponse: Prettify<CreateCompPassResponse> = {
        data: formattedData,
        meta: parsedMeta,
    };

    return formattedResponse;
};

/**
 * Creates a new batch of comp passes for a given facility.
 *
 * @see `/api/v1/facilities/{facilityId}/comp-pass/batches/`
 */
export const createCompPasses = async ({
    facilityId,
    requestParams,
}: {
    facilityId: number;
    requestParams: CreateCompPassRequestParams;
}) => {
    try {
        const formattedData = formatCreateCompPassRequest(requestParams);

        const response = await spotheroPost<RawCreateCompPassResponse[]>(
            `facilities/${facilityId}/comp-pass/batches/`,
            formattedData,
            {},
            false
        );

        const formattedResponse = formatCreateCompPassesResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
